// ==============================================
// Tauri → HTTP bridge for the ported NMEA Studio
// ==============================================
//
// The Studio was a Tauri desktop app: its UI called `invoke('command', args)`
// and a thin Rust layer (src-tauri/src/commands.rs) turned each command into an
// HTTP call against ne-server. Nothing in the Rust layer held state — it was a
// shim.
//
// So the port keeps the components and stores byte-for-byte and replaces only
// that shim. This module re-implements `invoke` plus the two Tauri plugins the
// store used, against the authenticated vortex-api relay.
//
// The command table below is transcribed from commands.rs — verb, path and body
// shape per command. If ne-server's routes change, that file is the source of
// truth to re-check against.
//
// @docs: docs/core/kb/NMEA_BLE_ACQUISITION_NAVLINK_BLUE.md

import { resolveApiUrl } from '../../lib/api-url';

const RELAY = () => `${resolveApiUrl()}/api/admin/emulator`;

type Verb = 'GET' | 'POST' | 'PUT' | 'DELETE';
type Args = Record<string, unknown>;

/** How a command builds its request body from the JS args. */
type BodyFn = (a: Args) => unknown;

interface CommandSpec {
  verb: Verb;
  /** Path after the relay. `{id}` is replaced with args.instanceId. */
  path: string;
  /** Omitted → no body (matches Rust's `post_empty`). */
  body?: BodyFn;
}

/** Pass the named arg straight through as the whole body. */
const whole = (key: string): BodyFn => (a) => a[key];

/** Build a snake_case body from camelCase args. */
const fields = (map: Record<string, string>): BodyFn => (a) => {
  const out: Args = {};
  for (const [wire, arg] of Object.entries(map)) out[wire] = a[arg];
  return out;
};

const I = '/api/i/{id}';

const COMMANDS: Record<string, CommandSpec> = {
  // ---- server level ----
  get_server_status: { verb: 'GET', path: '/api/status' },
  list_instances: { verb: 'GET', path: '/api/instances' },
  create_instance: {
    verb: 'POST',
    path: '/api/instances',
    body: fields({ owner: 'owner', name: 'name', visibility: 'visibility' }),
  },
  get_instance: { verb: 'GET', path: '/api/instances/{id}' },
  update_instance: {
    verb: 'PUT',
    path: '/api/instances/{id}',
    body: fields({ name: 'name', visibility: 'visibility', requester: 'requester' }),
  },
  delete_instance: {
    verb: 'DELETE',
    path: '/api/instances/{id}',
    body: fields({ requester: 'requester' }),
  },
  list_scenarios: { verb: 'GET', path: '/api/scenario/list' },
  delete_scenario: {
    verb: 'DELETE',
    path: '/api/scenario/delete',
    body: fields({ filename: 'filename' }),
  },

  // ---- simulation ----
  start_simulation: { verb: 'POST', path: `${I}/simulation/start` },
  stop_simulation: { verb: 'POST', path: `${I}/simulation/stop` },
  pause_simulation: { verb: 'POST', path: `${I}/simulation/pause` },
  resume_simulation: { verb: 'POST', path: `${I}/simulation/resume` },
  set_simulation_speed: {
    verb: 'POST',
    path: `${I}/simulation/speed`,
    body: fields({ multiplier: 'multiplier' }),
  },
  get_simulation_state: { verb: 'GET', path: `${I}/simulation/state` },

  // ---- vessel ----
  get_vessel_config: { verb: 'GET', path: `${I}/vessel/config` },
  set_vessel_config: { verb: 'POST', path: `${I}/vessel/config`, body: whole('config') },

  // ---- route ----
  get_route: { verb: 'GET', path: `${I}/route` },
  set_route: { verb: 'POST', path: `${I}/route/set`, body: whole('config') },
  set_route_waypoints: { verb: 'POST', path: `${I}/route/set-waypoints`, body: whole('config') },
  clear_route: { verb: 'POST', path: `${I}/route/clear` },
  set_repeat_route: {
    verb: 'POST',
    path: `${I}/route/repeat`,
    body: fields({ enabled: 'enabled' }),
  },

  // ---- weather ----
  set_manual_weather: { verb: 'POST', path: `${I}/weather/manual`, body: whole('weather') },
  load_grib_file: {
    verb: 'POST',
    path: `${I}/weather/grib`,
    body: fields({ file_path: 'filePath' }),
  },
  get_grib_info: { verb: 'GET', path: `${I}/weather/grib-info` },
  get_weather_at_position: {
    verb: 'POST',
    path: `${I}/weather/at-position`,
    body: fields({ lat: 'lat', lon: 'lon' }),
  },

  // ---- polars ----
  load_polar_file: {
    verb: 'POST',
    path: `${I}/polar/load`,
    body: fields({ file_path: 'filePath' }),
  },
  get_polar_data: { verb: 'GET', path: `${I}/polar/data` },
  get_boat_speed: {
    verb: 'POST',
    path: `${I}/polar/boat-speed`,
    body: fields({ tws: 'tws', twa: 'twa' }),
  },

  // ---- transports ----
  connect_signalk: { verb: 'POST', path: `${I}/transport/signalk/connect`, body: whole('config') },
  disconnect_signalk: { verb: 'POST', path: `${I}/transport/signalk/disconnect` },
  get_signalk_status: { verb: 'GET', path: `${I}/transport/signalk/status` },

  connect_nmea0183: { verb: 'POST', path: `${I}/transport/tcp-client/connect`, body: whole('config') },
  disconnect_nmea0183: { verb: 'POST', path: `${I}/transport/tcp-client/disconnect` },
  get_nmea0183_status: { verb: 'GET', path: `${I}/transport/tcp-client/status` },

  connect_udp_broadcast: { verb: 'POST', path: `${I}/transport/udp/connect`, body: whole('config') },
  disconnect_udp_broadcast: { verb: 'POST', path: `${I}/transport/udp/disconnect` },
  get_udp_broadcast_status: { verb: 'GET', path: `${I}/transport/udp/status` },

  start_tcp_server: { verb: 'POST', path: `${I}/transport/tcp-server/start`, body: whole('config') },
  stop_tcp_server: { verb: 'POST', path: `${I}/transport/tcp-server/stop` },
  get_tcp_server_status: { verb: 'GET', path: `${I}/transport/tcp-server/status` },

  // ---- data ----
  get_current_nmea_data: { verb: 'GET', path: `${I}/nmea/data` },

  // ---- faults ----
  get_fault_catalog: { verb: 'GET', path: `${I}/faults/catalog` },
  get_active_faults: { verb: 'GET', path: `${I}/faults/active` },
  enable_fault: {
    verb: 'POST',
    path: `${I}/faults/enable`,
    body: fields({ fault_id: 'faultId', severity: 'severity' }),
  },
  disable_fault: {
    verb: 'POST',
    path: `${I}/faults/disable`,
    body: fields({ fault_id: 'faultId' }),
  },
  clear_all_faults: { verb: 'POST', path: `${I}/faults/clear-all` },
  set_fault_severity: {
    verb: 'POST',
    path: `${I}/faults/severity`,
    body: fields({ fault_id: 'faultId', severity: 'severity' }),
  },

  // ---- AIS ----
  add_ais_target: { verb: 'POST', path: `${I}/ais/add`, body: whole('config') },
  remove_ais_target: { verb: 'POST', path: `${I}/ais/remove`, body: fields({ mmsi: 'mmsi' }) },
  clear_ais_targets: { verb: 'POST', path: `${I}/ais/clear` },
  get_ais_targets: { verb: 'GET', path: `${I}/ais/targets` },

  // ---- scenarios ----
  load_scenario: { verb: 'POST', path: `${I}/scenario/load`, body: whole('scenario') },
  load_scenario_file: {
    verb: 'POST',
    path: `${I}/scenario/load-file`,
    body: fields({ filename: 'filename' }),
  },
  save_scenario: { verb: 'GET', path: `${I}/scenario/save` },
  save_scenario_file: {
    verb: 'POST',
    path: `${I}/scenario/save-file`,
    body: fields({ filename: 'filename', name: 'name' }),
  },
};

/**
 * Commands that were purely local to the desktop shell. In the browser the
 * relay decides which server we talk to, so these are inert.
 */
const LOCAL_ONLY = new Set(['set_server_url', 'get_server_url']);

/**
 * Drop-in replacement for `@tauri-apps/api/core`'s `invoke`.
 *
 * Deliberately keeps Tauri's signature and its habit of rejecting with a
 * string, because the stores' `catch (err) { set({ error: String(err) }) }`
 * blocks were written against that.
 */
export async function invoke<T = void>(command: string, args: Args = {}): Promise<T> {
  if (LOCAL_ONLY.has(command)) return undefined as T;

  const spec = COMMANDS[command];
  if (!spec) throw `Unknown emulator command: ${command}`;

  const instanceId = (args.instanceId as string) ?? 'default';
  const path = spec.path.replace('{id}', encodeURIComponent(instanceId));

  const init: RequestInit = {
    method: spec.verb,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };
  if (spec.body) init.body = JSON.stringify(spec.body(args));

  let res: Response;
  try {
    res = await fetch(`${RELAY()}${path}`, init);
  } catch {
    throw `Could not reach the emulator relay (${command})`;
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw `${command} failed: ${res.status}${detail ? ` ${detail.slice(0, 200)}` : ''}`;
  }

  // Many control endpoints answer 200 with an empty body.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ==============================================
// Browser stand-ins for the Tauri dialog/fs plugins
// ==============================================
//
// The store used these for exactly one thing: exporting and importing a route
// as JSON on the operator's own machine. Server-side scenarios never went
// through here — they are ordinary HTTP calls. So a download and a file input
// reproduce the behaviour exactly.

/** Content staged by `open()`, keyed by the pseudo-path it returned. */
const stagedFiles = new Map<string, string>();

interface DialogFilter {
  name: string;
  extensions: string[];
}

export async function save(opts?: {
  filters?: DialogFilter[];
  defaultPath?: string;
}): Promise<string | null> {
  // A browser cannot ask where to put the file — the download does that. Hand
  // back the intended filename so writeTextFile knows what to call it.
  return opts?.defaultPath ?? 'download.json';
}

export async function writeTextFile(path: string, contents: string): Promise<void> {
  const blob = new Blob([contents], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = path.split(/[\\/]/).pop() || 'download.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function open(opts?: {
  filters?: DialogFilter[];
  multiple?: boolean;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    const exts = opts?.filters?.flatMap((f) => f.extensions.map((e) => `.${e}`)) ?? [];
    if (exts.length) input.accept = exts.join(',');

    // A cancelled picker fires no 'change' event; `cancel` covers modern
    // browsers, and the store treats a null path as "user backed out".
    input.addEventListener('cancel', () => resolve(null), { once: true });
    input.addEventListener(
      'change',
      async () => {
        const file = input.files?.[0];
        if (!file) return resolve(null);
        const key = `browser://${file.name}`;
        stagedFiles.set(key, await file.text());
        resolve(key);
      },
      { once: true },
    );

    input.click();
  });
}

export async function readTextFile(path: string): Promise<string> {
  const staged = stagedFiles.get(path);
  if (staged === undefined) throw `No staged file for ${path}`;
  stagedFiles.delete(path);
  return staged;
}
